import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getFacturas, descargarFacturasExport } from "../api"
import { formatCurrency, formatNumber, formatPercent, reglaConfig, colorPara, rangoDelMes } from "../helpers"
import CopiarCufe from "../CopiarCufe"

const PAGE_SIZE = 10000

const ReglaBadge = ({ regla }) => {
  const cfg = reglaConfig(regla)
  return (
    <span
      style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 6,
        background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.3,
      }}
      title={cfg.desc}
    >
      {cfg.label}
    </span>
  )
}

ReglaBadge.propTypes = { regla: PropTypes.string }

const AjustesCards = ({ ajustes }) => {
  if (!ajustes) return null
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      <div style={{ flex: "1 1 220px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, borderLeft: "4px solid #10b981" }}>
        <div style={{ fontSize: 11, color: "#065f46", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
          <i className="mdi mdi-check-circle me-1" />
          Regla 1: ajuste único
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 6 }}>{formatNumber(ajustes.regla1_factura_unica)}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Monto: {formatCurrency(ajustes.regla1_monto_ajustado)}</div>
      </div>
      <div style={{ flex: "1 1 220px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, borderLeft: "4px solid #3b82f6" }}>
        <div style={{ fontSize: 11, color: "#1e40af", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
          <i className="mdi mdi-cash-sync me-1" />
          Regla 2: efectivo ajustado
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 6 }}>{formatNumber(ajustes.regla2_efectivo_mixto)}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Monto: {formatCurrency(ajustes.regla2_monto_ajustado)}</div>
      </div>
      <div style={{ flex: "1 1 220px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, borderLeft: "4px solid #ef4444" }}>
        <div style={{ fontSize: 11, color: "#991b1b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
          <i className="mdi mdi-alert me-1" />
          Regla 3: sin ajuste
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 6 }}>{formatNumber(ajustes.regla3_sin_ajuste)}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Diferencia: {formatCurrency(ajustes.regla3_monto_diferencia)}</div>
      </div>
      <div style={{ flex: "1 1 220px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
          <i className="mdi mdi-receipt-text me-1" />
          Total facturas evaluadas
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 6 }}>{formatNumber(ajustes.total_facturas_evaluadas)}</div>
      </div>
    </div>
  )
}

AjustesCards.propTypes = { ajustes: PropTypes.object }

const Combinaciones = ({ combinaciones }) => {
  if (!combinaciones || combinaciones.length === 0) return null
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
      <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
        <i className="mdi mdi-vector-combine me-2 text-primary" />
        Combinaciones de métodos en una misma factura
      </h6>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {combinaciones.slice(0, 12).map((c, i) => (
          <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
              {(c.metodos || []).map((m, j) => (
                <span key={j} style={{ background: colorPara(j) + "22", color: colorPara(j), padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                  {m}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ color: "#64748b" }}>{formatNumber(c.facturas)} facturas</span>
              <span style={{ color: "#0f172a", fontWeight: 700 }}>{formatCurrency(c.valor_total)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

Combinaciones.propTypes = { combinaciones: PropTypes.array }

const FilaFactura = ({ f, expandida, onToggle, showSucursal = true }) => {
  const reglas = (f.metodos_pago || []).map(m => m.regla_aplicada)
  const reglaPrincipal = reglas[0]

  return (
    <>
      <tr
        style={{ background: expandida ? "#eff6ff" : "#fff", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
        onClick={onToggle}
      >
        <td style={{ padding: "10px 12px", textAlign: "center", color: "#64748b", paddingLeft: showSucursal ? 12 : 32 }}>
          <i className={`mdi mdi-chevron-${expandida ? "down" : "right"}`} style={{ fontSize: 18 }} />
        </td>
        <td style={{ padding: "10px 12px", color: "#0f172a", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}>
          {f.prefijo}-{f.folio}
        </td>
        <td style={{ padding: "10px 12px", color: "#475569", fontSize: 12 }}>{f.fecha}</td>
        {showSucursal && (
          <td style={{ padding: "10px 12px", color: "#475569" }}>
            <i className="mdi mdi-store text-primary me-1" />
            {f.sucursal || "—"}
          </td>
        )}
        <td style={{ padding: "10px 12px", color: "#0f172a", fontWeight: 600, textAlign: "right" }}>{formatCurrency(f.total)}</td>
        <td style={{ padding: "10px 12px", textAlign: "center" }}>
          <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
            {f.cantidad_metodos || (f.metodos_pago || []).length}
          </span>
        </td>
        <td style={{ padding: "10px 12px", textAlign: "center" }}>
          <ReglaBadge regla={reglaPrincipal} />
        </td>
      </tr>
      {expandida && (
        <tr style={{ background: "#fafafa" }}>
          <td />
          <td colSpan={showSucursal ? 6 : 5} style={{ padding: "12px 16px" }}>
            <div style={{
              marginBottom: 10, fontSize: 12, color: "#64748b",
              display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
            }}>
              <span><strong>Receptor:</strong> {f.nombre_receptor || "—"}</span>
              <span><strong>NIT:</strong> {f.nit_receptor || "—"}</span>
              <CopiarCufe cufe={f.cufe} />
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "#64748b", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>Método</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>Valor original</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>Valor ajustado</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", color: "#64748b", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>Δ</th>
                  <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748b", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}>Regla</th>
                </tr>
              </thead>
              <tbody>
                {(f.metodos_pago || []).map((m, i) => {
                  const dif = (Number(m.valor) || 0) - (Number(m.valor_original) || 0)
                  const ajustado = Math.abs(dif) > 0.01
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 10px", color: "#0f172a", fontWeight: 500 }}>
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colorPara(i), marginRight: 8 }} />
                        {m.tipo}
                      </td>
                      <td style={{
                        padding: "6px 10px", textAlign: "right", color: ajustado ? "#94a3b8" : "#475569",
                        textDecoration: ajustado ? "line-through" : "none",
                      }}>
                        {formatCurrency(m.valor_original)}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>
                        {formatCurrency(m.valor)}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: dif > 0 ? "#10b981" : dif < 0 ? "#ef4444" : "#94a3b8" }}>
                        {dif !== 0 ? `${dif > 0 ? "+" : ""}${formatCurrency(dif)}` : "—"}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "center" }}>
                        <ReglaBadge regla={m.regla_aplicada} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

FilaFactura.propTypes = {
  f: PropTypes.object,
  expandida: PropTypes.bool,
  onToggle: PropTypes.func,
  showSucursal: PropTypes.bool,
}

const FacturasTab = ({ filtros }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(0)
  const [expandidas, setExpandidas] = useState(new Set())
  const [filtroRegla, setFiltroRegla] = useState("")
  const [agrupado, setAgrupado] = useState(false)
  const [sucursalesExpand, setSucursalesExpand] = useState(new Set())
  const [exportando, setExportando] = useState(null)
  const [exportInfo, setExportInfo] = useState(null)

  const cargar = useCallback(async (pageIdx) => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      // index.jsx siempre inyecta fecha_inicio/fecha_fin (sea modo mes o rango).
      // Fallback a rangoDelMes por si llega un filtros legacy.
      const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
      const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
      const { data: response } = await getFacturas({
        nitemisor: filtros.nitemisor,
        fecha_inicio,
        fecha_fin,
        sucursal: filtros.sucursal,
        tipo_metodo: filtros.tipo_metodo,
        campo_fecha: filtros.campo_fecha,
        limit: PAGE_SIZE,
        offset: pageIdx * PAGE_SIZE,
        incluir: "combinaciones,por_sucursal",
      })
      setData(response)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar las facturas.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros])

  useEffect(() => {
    setPage(0)
    setExpandidas(new Set())
    cargar(0)
  }, [cargar])

  const handleExportar = async (formato) => {
    if (!filtros?.nitemisor || exportando) return
    setExportando(formato)
    setExportInfo(null)
    setError("")
    try {
      const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
      const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
      const info = await descargarFacturasExport({
        nitemisor: filtros.nitemisor,
        fecha_inicio, fecha_fin,
        sucursal: filtros.sucursal,
        tipo_metodo: filtros.tipo_metodo,
        campo_fecha: filtros.campo_fecha,
        formato,
      })
      setExportInfo({ formato, totalRows: info.totalRows, filename: info.filename })
      setTimeout(() => setExportInfo(null), 6000)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const facturasRaw = data?.facturas || []

  const facturas = useMemo(() => {
    if (!filtroRegla) return facturasRaw
    return facturasRaw.filter(f => (f.metodos_pago || []).some(m => m.regla_aplicada === filtroRegla))
  }, [facturasRaw, filtroRegla])

  // Agrupar por sucursal cuando está activo
  const grupos = useMemo(() => {
    if (!agrupado) return null
    const map = new Map()
    facturas.forEach(f => {
      const key = f.sucursal || "(Sin sucursal)"
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(f)
    })
    return Array.from(map.entries())
      .map(([sucursal, items]) => ({
        sucursal,
        items,
        total: items.reduce((s, f) => s + (Number(f.total) || 0), 0),
      }))
      .sort((a, b) => b.total - a.total)
  }, [agrupado, facturas])

  const toggleSucursal = nombre => {
    setSucursalesExpand(prev => {
      const next = new Set(prev)
      if (next.has(nombre)) next.delete(nombre)
      else next.add(nombre)
      return next
    })
  }

  // Auto-expandir todas las sucursales al activar agrupación
  useEffect(() => {
    if (agrupado && grupos) {
      setSucursalesExpand(new Set(grupos.map(g => g.sucursal)))
    }
  }, [agrupado]) // eslint-disable-line

  const total = data?.paginacion?.total_disponible || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const toggle = cufe => {
    setExpandidas(prev => {
      const next = new Set(prev)
      if (next.has(cufe)) next.delete(cufe)
      else next.add(cufe)
      return next
    })
  }

  const irPagina = (nueva) => {
    if (nueva < 0 || nueva >= totalPages) return
    setPage(nueva)
    setExpandidas(new Set())
    cargar(nueva)
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#dc2626", background: "#fef2f2", borderRadius: 10 }}>
        <i className="mdi mdi-alert-circle me-2" />
        {error}
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
        <Spinner color="primary" />
        <div className="mt-2">Cargando facturas…</div>
      </div>
    )
  }

  if (!data) return null

  const ajustes = data.ajustes_aplicados
  const combinaciones = data.combinaciones

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cards de las 3 reglas */}
      <AjustesCards ajustes={ajustes} />

      {/* Combinaciones */}
      <Combinaciones combinaciones={combinaciones} />

      {/* Tabla de facturas */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-receipt-text-outline me-2 text-primary" />
            Facturas ({formatNumber(facturas.length)}
            {facturas.length !== facturasRaw.length && ` de ${formatNumber(facturasRaw.length)} en página`}
            {" "}/ {formatNumber(total)} totales)
          </h6>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={() => handleExportar("xlsx")}
                disabled={!!exportando || !filtros?.nitemisor}
                title="Descargar Excel completo del período (puede tardar varios segundos)"
              >
                {exportando === "xlsx" ? (
                  <><Spinner size="sm" className="me-1" /> Generando…</>
                ) : (
                  <><i className="mdi mdi-file-excel me-1" /> Excel</>
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleExportar("csv")}
                disabled={!!exportando || !filtros?.nitemisor}
                title="Descargar CSV completo del período"
              >
                {exportando === "csv" ? (
                  <><Spinner size="sm" className="me-1" /> Generando…</>
                ) : (
                  <><i className="mdi mdi-file-delimited me-1" /> CSV</>
                )}
              </button>
            </div>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 200, fontSize: 13 }}
              value={filtroRegla}
              onChange={e => setFiltroRegla(e.target.value)}
            >
              <option value="">Todas las reglas</option>
              <option value="ajuste_unico">Solo regla 1 (único)</option>
              <option value="ajuste_efectivo">Solo regla 2 (efectivo)</option>
              <option value="sin_ajuste">Solo regla 3 (sin ajuste)</option>
            </select>
            <div className="form-check form-switch m-0">
              <input
                type="checkbox"
                className="form-check-input"
                role="switch"
                id="agruparSucursal"
                checked={agrupado}
                onChange={e => setAgrupado(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="agruparSucursal" style={{ color: "#475569" }}>
                Agrupar por sucursal
              </label>
            </div>
            {agrupado && grupos && grupos.length > 0 && (() => {
              const todasExpandidas = sucursalesExpand.size === grupos.length
              return (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    setSucursalesExpand(
                      todasExpandidas ? new Set() : new Set(grupos.map(g => g.sucursal)),
                    )
                  }
                  title={todasExpandidas ? "Contraer todas las sucursales" : "Expandir todas las sucursales"}
                >
                  <i className={`mdi mdi-${todasExpandidas ? "collapse-all" : "expand-all"} me-1`} />
                  {todasExpandidas ? "Contraer todo" : "Expandir todo"}
                </button>
              )
            })()}
            <span style={{ fontSize: 12, color: "#64748b" }}>
              Página {page + 1} / {totalPages}
            </span>
          </div>
        </div>

        {exportInfo && (
          <div style={{
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "10px 14px", borderRadius: 8,
            marginBottom: 12, fontSize: 13,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
            <span>
              Descarga lista{exportInfo.totalRows ? `: ${formatNumber(exportInfo.totalRows)} filas` : ""}
              {exportInfo.filename ? ` · ${exportInfo.filename}` : ""}
            </span>
            <button
              type="button"
              className="btn-close ms-auto"
              style={{ fontSize: 10 }}
              onClick={() => setExportInfo(null)}
            />
          </div>
        )}

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ maxHeight: 580, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "10px 12px", width: 40 }} />
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Folio</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Fecha</th>
                  {!agrupado && (
                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Sucursal</th>
                  )}
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Total</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>#</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600 }}>Regla</th>
                </tr>
              </thead>
              <tbody>
                {facturas.length === 0 && (
                  <tr><td colSpan={agrupado ? 6 : 7} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
                    Sin facturas para los filtros aplicados.
                  </td></tr>
                )}

                {!agrupado && facturas.map(f => (
                  <FilaFactura
                    key={f.cufe}
                    f={f}
                    expandida={expandidas.has(f.cufe)}
                    onToggle={() => toggle(f.cufe)}
                    showSucursal
                  />
                ))}

                {agrupado && grupos && grupos.map(g => {
                  const exp = sucursalesExpand.has(g.sucursal)
                  return (
                    <React.Fragment key={g.sucursal}>
                      <tr
                        style={{ background: "#f1f5f9", cursor: "pointer", borderBottom: "1px solid #cbd5e1" }}
                        onClick={() => toggleSucursal(g.sucursal)}
                      >
                        <td style={{ padding: "10px 12px", textAlign: "center", color: "#475569" }}>
                          <i className={`mdi mdi-chevron-${exp ? "down" : "right"}`} style={{ fontSize: 18 }} />
                        </td>
                        <td colSpan={3} style={{ padding: "10px 12px", color: "#0f172a", fontWeight: 700 }}>
                          <i className="mdi mdi-store text-primary me-2" />
                          {g.sucursal}
                          <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8, fontSize: 12 }}>
                            ({g.items.length} factura{g.items.length !== 1 ? "s" : ""})
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 700 }}>
                          {formatCurrency(g.total)}
                        </td>
                        <td colSpan={2} />
                      </tr>
                      {exp && g.items.map(f => (
                        <FilaFactura
                          key={f.cufe}
                          f={f}
                          expandida={expandidas.has(f.cufe)}
                          onToggle={() => toggle(f.cufe)}
                          showSucursal={false}
                        />
                      ))}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(page - 1)} disabled={page === 0 || loading}>
              <i className="mdi mdi-chevron-left" /> Anterior
            </button>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {loading && <Spinner size="sm" className="me-2" />}
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {formatNumber(total)}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(page + 1)} disabled={page + 1 >= totalPages || loading}>
              Siguiente <i className="mdi mdi-chevron-right" />
            </button>
          </div>
        )}

        {/* Distribución por método del set actual */}
        {data.resumen?.distribucion_porcentual && (
          <div style={{ marginTop: 16, padding: 12, background: "#f8fafc", borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
              Distribución del lote actual
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {Object.entries(data.resumen.distribucion_porcentual).map(([m, pct], i) => (
                <span key={m} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: colorPara(i) }} />
                  <strong>{m}</strong> · {formatPercent(pct)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

FacturasTab.propTypes = { filtros: PropTypes.object }

export default FacturasTab
